import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { Container, Loading, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import ClassSummaryFilters from './components/ClassSummaryFilters';
import ClassSummaryTable from './components/ClassSummaryTable';
import type { ClassSummaryResultDTO } from './models/classSummaryDTOs';
import { useAuth } from '@/src/app/context/useAuth';

export interface FilterState {
    gradeLevelMasterId: number;
    districtId: number;
    schoolId: number;
    classId: number;
    examTypeMasterId: number;
    examIds: number[];
}

const ClassSummaryPage: React.FC = () => {
    document.title = 'Class Summary';

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const [filterState, setFilterState] = useState<FilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    classId: parsed.classId || 0,
                    examTypeMasterId: parsed.examTypeMasterId || 0,
                    examIds: parsed.examIds || [],
                    viewMode: parsed.viewMode || 1,
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            gradeLevelMasterId: 0,
            districtId: 0,
            schoolId: 0,
            classId: 0,
            examTypeMasterId: 0,
            examIds: [],
            viewMode: 1,
        };
    });

    useEffect(() => {
        if (!userId) return;
        const universalFilters = {
            gradeLevelMasterId: filterState.gradeLevelMasterId,
            districtId: filterState.districtId,
            schoolId: filterState.schoolId,
            classId: filterState.classId,
        };
        try {
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, userId, UNIVERSAL_FILTER_KEY]);

    const [tableData, setTableData] = useState<ClassSummaryResultDTO[]>([]);

    const refreshTableData = useCallback((state: FilterState) => {
        setError('');
        setLoaded(false);
        setHasSearched(true);
        const endpoint = `reports/classSummary?districtId=${state.districtId}&schoolId=${state.schoolId}&classId=${state.classId}&gradeId=${state.gradeLevelMasterId}&examTypeMasterId=${state.examTypeMasterId}&examId=${state.examIds[0] || 0}`;
        apiRequest<ClassSummaryResultDTO[]>('GET', endpoint)
            .then((res) => {
                if (Array.isArray(res.data)) {
                    setTableData(res.data);
                } else if (res.data && Array.isArray((res.data as { data: unknown }).data)) {
                    setTableData((res.data as { data: ClassSummaryResultDTO[] }).data);
                } else {
                    setTableData([]);
                }
            })
            .catch(() => {
                setError('Failed to load Class Summary Report');
                setTableData([]);
            })
            .finally(() => {
                setLoaded(true);
            });
    }, []);

    return (
        <Container>
            <h1 className="page-header">Class Summary Report</h1>
            <br />

            <ClassSummaryFilters
                refreshTableData={refreshTableData}
                filterState={filterState}
                setFilterState={setFilterState}
            />

            {hasSearched && !loaded && <Loading message="Loading Class Summary Report..." />}
            {error && (
                <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <ErrorMessage error={error} />
                </Box>
            )}
            <ClassSummaryTable
                data={loaded ? tableData : []}
                isLoading={false}
                error={error}
                emptyMessage={hasSearched && loaded ? 'No class summary data found.' : ''}
            />
        </Container>
    );
};

export default ClassSummaryPage;
